// Login gate: blocks the dashboard until a valid @delricht.com Google identity exists.
// Renders the Google Sign-In screen otherwise. UI-only gate — API auth is unchanged.
import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { B, F, R, S, T } from '@/theme/brand';
import { appConfig } from '@/utils/config';
import { useGoogleAuth } from './GoogleAuthContext';

const LoginGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, acceptCredential } = useGoogleAuth();
  const [error, setError] = useState<string | null>(null);
  const domain = appConfig.allowedGoogleDomain;
  const configured = !!appConfig.googleClientId;

  if (isAuthenticated) return <>{children}</>;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: B.bg,
        fontFamily: F.body,
        padding: S.lg,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: B.card,
          borderRadius: R.lg,
          border: `1px solid ${B.border}`,
          boxShadow: '0 10px 40px rgba(10,26,46,0.12)',
          overflow: 'hidden',
        }}
      >
        <div style={{ background: B.blue, padding: `${S.xl}px ${S.lg}px`, textAlign: 'center' }}>
          <div style={{ fontFamily: F.head, fontWeight: 700, fontSize: T.h2, color: '#fff' }}>
            DelRicht Scheduling
          </div>
          <div style={{ fontSize: T.sm, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>
            Clinical Trial Recruitment Dashboard
          </div>
        </div>

        <div style={{ padding: S.xl, textAlign: 'center' }}>
          <div style={{ fontFamily: F.head, fontWeight: 600, fontSize: T.h3, color: B.text, marginBottom: S.xs }}>
            Sign in to continue
          </div>
          <p style={{ color: B.textMute, fontSize: T.sm, margin: `0 0 ${S.lg}px` }}>
            Use your <strong>@{domain}</strong> Google account.
          </p>

          {configured ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={(cred) => {
                  setError(null);
                  const err = cred.credential
                    ? acceptCredential(cred.credential)
                    : 'No credential returned by Google. Please try again.';
                  if (err) setError(err);
                }}
                onError={() => setError('Google sign-in failed. Please try again.')}
                hosted_domain={domain}
                theme="filled_blue"
                shape="pill"
                text="signin_with"
              />
            </div>
          ) : (
            <div style={{ color: B.red, fontSize: T.sm }}>
              OAuth is not configured (missing <code>VITE_GOOGLE_CLIENT_ID</code>). Contact an administrator.
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                marginTop: S.lg,
                padding: `${S.sm}px ${S.md}px`,
                background: B.redLight,
                border: `1px solid ${B.red}33`,
                borderRadius: R.md,
                color: B.text,
                fontSize: T.sm,
                textAlign: 'left',
              }}
            >
              <i className="bi bi-exclamation-octagon" style={{ color: B.red, marginRight: S.xs }} />
              {error}
            </div>
          )}

          <p style={{ color: B.textMute, fontSize: T.tiny, marginTop: S.lg, marginBottom: 0 }}>
            Access is limited to authorized DelRicht staff.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginGate;
